/** The design system's pieces (docs/DESIGN.md §3). Styling lives in src/styles.css. */
import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useInRouterContext } from "react-router-dom";
import { Mascot } from "./Brand.tsx";
import { ChopCleaver } from "./Chop.tsx";
import { Icon, type IconName } from "./Icons.tsx";
import { UNDO_SECONDS, mergeLanded, type LandedCard, type LandedEvent, type LandedViewer } from "@logwave/core";
import { LandedCardView } from "./Landed.tsx";
import { barFill, prefersReducedMotion } from "../lib/motion.ts";
import { installSheetScroll } from "./sheetScroll.ts";

/* ───────── page and sections ───────── */

export function Page({ title, kicker, lede, actions, children, narrow, wide }: { title: ReactNode; kicker?: ReactNode; lede?: ReactNode; actions?: ReactNode; children: ReactNode; narrow?: boolean; /** A table-heavy screen for staff on a desktop (the Data dashboard). */ wide?: boolean }) {
  return (
    <div className={`page${narrow ? " page-narrow" : ""}${wide ? " page-wide" : ""}`}>
      <header className="page-head">
        <div>
          {kicker && <p className="kicker">{kicker}</p>}
          <h1>{title}</h1>
          {lede && <p className="lede">{lede}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

/** Small-caps label above a Garamond heading. */
export function Section({ id, kicker, title, aside, children, className }: { id?: string; kicker?: ReactNode; title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  const hid = useId();
  return (
    <section className={`section${className ? ` ${className}` : ""}`} aria-labelledby={title ? hid : undefined} id={id}>
      {(title || kicker || aside) && (
        <div className="section-head">
          <div>
            {kicker && <p className="kicker">{kicker}</p>}
            {title && <h2 id={hid}>{title}</h2>}
          </div>
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/* ───────── buttons ───────── */

/**
 * One button system (DESIGN §3). Primary = ink fill (one per screen or card), Secondary = outlined, Quiet = text,
 * Danger = stop fill. Labels say exactly what happens, verb + object ("Test sanitizer", "Call Sam"), never "Go" or "Open".
 * Pressed scales to .98; busy swaps the icon for a spinner and keeps the label; disabled can say why (`reason`).
 */
/** "fixlog": the maroon "Fix and log it now" family (core fixgo.ts; owner, 2026-10-15): the one bold fix button, never the stop red or the primary ink. */
export type Variant = "primary" | "secondary" | "quiet" | "danger" | "go" | "ghost" | "ok" | "fixlog";
const variantClass = (v: Variant) => (v === "primary" ? "" : v === "ghost" ? " btn-quiet" : v === "ok" ? " btn-go" : ` btn-${v}`);
const btnClass = (v: Variant, o: { big?: boolean; block?: boolean; small?: boolean; className?: string }) =>
  `btn${variantClass(v)}${o.big ? " btn-big" : ""}${o.block ? " btn-block" : ""}${o.small ? " btn-sm" : ""}${o.className ? ` ${o.className}` : ""}`;

type BtnOwn = { variant?: Variant; big?: boolean; block?: boolean; small?: boolean; busy?: boolean; icon?: IconName; reason?: string; /** "tap": the reason shows only after the person taps the disabled button (use when the field already has a value, so a standing line never reads as an error). Default: always under a disabled button. */ reasonOn?: "tap"; /** Icon after the label (forward motion: "Next step >"). */ iconEnd?: boolean };

export function Button({ variant = "primary", big, block, small, busy, icon, iconEnd, className, children, disabled, reason, reasonOn, onClick, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & BtnOwn) {
  const rid = useId();
  const [tried, setTried] = useState(false);
  useEffect(() => { if (!disabled) setTried(false); }, [disabled]);
  // Tap mode keeps the button focusable and tappable (aria-disabled, not `disabled`) so a tap can answer "why not?".
  const soft = reasonOn === "tap" && !!reason && !!disabled && !busy;
  const showReason = !!reason && !!disabled && !busy && (!soft || tried);
  const btn = (
    <button type="button" className={btnClass(variant, { big, block, small, className })}
      disabled={soft ? undefined : disabled || busy} aria-disabled={soft || undefined} aria-busy={busy || undefined} title={showReason ? reason : rest.title} aria-describedby={showReason ? rid : undefined}
      onClick={soft ? (e) => { e.preventDefault(); setTried(true); } : onClick} {...rest}>
      {busy ? <span className="spin" aria-hidden="true" /> : icon && !iconEnd ? <Icon name={icon} size={small ? 18 : 20} /> : null}
      <span className="btn-label">{children}</span>
      {!busy && icon && iconEnd && <Icon name={icon} size={small ? 18 : 20} />}
    </button>
  );
  if (!showReason) return btn;
  return <span className={`btn-wrap${block ? " btn-wrap-block" : ""}`}>{btn}<span id={rid} className="btn-reason" role="status"><Icon name="info" size={14} />{reason}</span></span>;
}

/** True when any button's label is wider than its box (scrollWidth over clientWidth): the pair can't sit side by side with whole words. */
export function pairCut(items: { scrollWidth: number; clientWidth: number }[]): boolean {
  return items.some((i) => i.scrollWidth > i.clientWidth + 1);
}

/**
 * Two answers side by side in equal widths (Yes / No, "Save: it beeped" / "Save: it did not beep"), each label on ONE line.
 * If either label doesn't fit that way (a narrow screen, large text, a longer translation), the pair stacks: one full-width
 * button under the other, where the words can wrap at a space. A label is never squeezed into two lines in a half-width box.
 * It measures in the side-by-side layout every time the container's size or its words change, then picks.
 */
export function ButtonPair({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("button-pair-stack");
    el.classList.toggle("button-pair-stack", pairCut([...el.querySelectorAll<HTMLElement>(".btn")]));
  }, []);
  useLayoutEffect(measure); // every render: the words may have changed
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : undefined;
    ro?.observe(el); // the container's width doesn't change when the pair stacks, so this can't loop
    void document.fonts?.ready.then(measure);
    return () => ro?.disconnect();
  }, [measure]);
  return <div ref={ref} className={`button-pair${className ? ` ${className}` : ""}`} {...rest}>{children}</div>;
}

/** A router link that looks like a button. */
export function LinkButton({ to, variant = "primary", big, block, small, icon, iconEnd, children, className }: { to: string; children: ReactNode; className?: string } & Omit<BtnOwn, "busy" | "reason">) {
  return (
    <Link to={to} className={btnClass(variant, { big, block, small, className })}>
      {icon && !iconEnd && <Icon name={icon} size={small ? 18 : 20} />}<span className="btn-label">{children}</span>{icon && iconEnd && <Icon name={icon} size={small ? 18 : 20} />}
    </Link>
  );
}

/** A real link (tel:, sms:, https:) that looks like a button. External https links open in a new tab. */
export function AnchorButton({ href, variant = "primary", big, block, small, icon, children, className, onClick, testId, rel, label }: { href: string; children: ReactNode; className?: string; /** Runs as the link opens (e.g. record a Nag as Messages opens); never blocks it. */ onClick?: () => void; testId?: string; /** An outside link's rel from core (a tagged Amazon link is "sponsored"). */ rel?: string; /** Screen-reader name when the words need more ("opens in a new tab"). */ label?: string } & Omit<BtnOwn, "busy" | "reason">) {
  const external = href.startsWith("https:");
  return (
    <a href={href} className={btnClass(variant, { big, block, small, className })} {...(external ? { target: "_blank", rel: rel ?? "noopener noreferrer" } : {})} {...(label ? { "aria-label": label } : {})} {...(onClick ? { onClick } : {})} {...(testId ? { "data-testid": testId } : {})}>
      {icon && <Icon name={icon} size={small ? 18 : 20} />}<span className="btn-label">{children}</span>
    </a>
  );
}

/* ───────── cards, pills, notices ───────── */

export type Tone = "go" | "warn" | "stop" | "info" | "neutral" | "ink" | "line";
/** Legacy tone names used by older screens. */
const TONE_ALIAS: Record<string, Tone> = { ok: "go", bad: "stop", accent: "ink" };
const toneOf = (t: string | undefined): Tone => (t ? TONE_ALIAS[t] ?? (t as Tone) : "neutral");
const TONE_ICON: Partial<Record<Tone, IconName>> = { go: "check", warn: "alert", stop: "x", info: "info" };

export function Card({ children, className, edge, as = "section", flat }: { children: ReactNode; className?: string; edge?: "stop" | "warn" | "go" | "info"; as?: "section" | "div" | "article"; flat?: boolean; tone?: string }) {
  const Tag = as;
  return <Tag className={`card${flat ? " card-flat" : ""}${edge ? ` edge edge-${edge}` : ""}${className ? ` ${className}` : ""}`}>{children}</Tag>;
}

/** Status pill: icon + word, safety colors. Never color alone. */
export function Pill({ tone = "neutral", icon, children, title }: { tone?: Tone | "ok" | "bad" | "accent"; icon?: IconName | null; children: ReactNode; title?: string }) {
  const t = toneOf(tone);
  const ic = icon === null ? undefined : icon ?? TONE_ICON[t];
  return <span className={`pill${t !== "neutral" ? ` pill-${t}` : ""}`} title={title}>{ic && <Icon name={ic} size={15} />}{children}</span>;
}
export const Badge = Pill;

/**
 * `pulse`: a stop notice that has just appeared (a bad cooling reading, a recall match) gets one ring pulse, once (`alert-in`,
 * styles.css). Only the stop tone can pulse: a good result never gets the warning signal.
 */
export function Notice({ tone = "info", icon, title, children, action, pulse }: { tone?: "info" | "warn" | "stop" | "go"; icon?: IconName; title?: ReactNode; children?: ReactNode; action?: ReactNode; pulse?: boolean }) {
  return (
    <div className={`notice notice-${tone}${pulse && tone === "stop" ? " alert-in" : ""}`} role={tone === "stop" ? "alert" : undefined}>
      <Icon name={icon ?? TONE_ICON[tone] ?? "info"} />
      <div className="notice-body">
        {title && <strong>{title}</strong>}
        {children}
        {action && <div className="row">{action}</div>}
      </div>
    </div>
  );
}

export function Why({ children, to }: { children: ReactNode; to?: string }) {
  return <p className="why"><Icon name="info" size={17} /><span>{children}{to && <> <Link to={to}>Read the guide</Link></>}</span></p>;
}

/* ───────── content kinds (DESIGN §3a) ─────────
 * Seven kinds, one look each: Problem · Step · Answer · Action (Button/LinkButton/AnchorButton above) · Setup hint ·
 * Outside link · Info. Core contentKinds.ts decides which kind a piece of content is; these only draw it.
 */

/** Problem: what's wrong. A heading with the stop color, an icon and a word; answered → go, check, "Handled". Not tappable. */
export function ProblemHeading({ children, done, level = 3, id }: { children: ReactNode; done?: boolean; level?: 3 | 4; id?: string }) {
  const H = level === 3 ? "h3" : "h4";
  return (
    <H id={id} className={`k-problem${done ? " is-done" : ""}`}>
      <Icon name={done ? "check" : "alert"} size={20} />
      <span className="k-problem-text">{children}</span>
      <span className="k-problem-word">{done ? "Handled" : "To fix"}</span>
    </H>
  );
}

/** Step: something you do with your hands. Plain text with a hand marker (or a number), and an optional "How?" text link. Never a button. */
export function StepList({ steps, extra }: { steps: { text: string; howTo?: string }[]; /** Under each step's words: the supply it names (components/StepSupply.tsx SupplyLine). */ extra?: (text: string) => ReactNode }) {
  if (!steps.length) return null;
  const numbered = steps.length > 1;
  return (
    <ol className={`k-steps${numbered ? " is-numbered" : ""}`}>
      {steps.map((st, i) => (
        <li key={st.text} className="k-step">
          <span className="k-step-mark" aria-hidden="true">{numbered ? i + 1 : <Icon name="hands" size={18} />}</span>
          <span className="k-step-text">{st.text}{extra?.(st.text)}</span>
          {st.howTo && <Link className="k-how" to={st.howTo} aria-label={`How? ${st.text}`}>How?</Link>}
        </li>
      ))}
    </ol>
  );
}

/**
 * Answer: what you did, for the record. A labeled radio group of chips, the only chip-shaped thing on a card. The chosen
 * chip is ink-filled with a check (never color alone). Arrow keys move and choose, like native radios; one Tab stop.
 */
export function AnswerGroup({ label = "What did you do?", name, options, value, onChange }: { label?: string; /** Accessible name (unique per problem). */ name?: string; options: string[]; value?: string; onChange(v: string): void }) {
  const id = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const cur = options.indexOf(value ?? "");
  const move = (i: number) => { const n = (i + options.length) % options.length; onChange(options[n]!); refs.current[n]?.focus(); };
  return (
    <div className="k-answers">
      <div id={id} className="k-answers-label">{label}</div>
      <div className="chips" role="radiogroup" {...(name ? { "aria-label": name } : { "aria-labelledby": id })}>
        {options.map((o, i) => (
          <button key={o} ref={(el) => { refs.current[i] = el; }} type="button" role="radio" className="chip k-answer" aria-checked={value === o}
            tabIndex={cur === i || (cur < 0 && i === 0) ? 0 : -1} onClick={() => onChange(o)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); move(i + 1); }
              else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); move(i - 1); }
            }}>
            <span className="k-answer-dot" aria-hidden="true">{value === o && <Icon name="check" size={14} />}</span>{o}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Setup hint: something missing in setup. Muted text; managers get an inline "Add it" link straight to the Settings field. */
export function SetupHint({ text, link }: { text: string; link?: { label: string; to: string } }) {
  return (
    <p className="k-setup">
      <Icon name="settings" size={16} />
      <span>{text}{link && <> <Link to={link.to}>{link.label}</Link></>}</span>
    </p>
  );
}

/**
 * Outside link: leaves the app. A text link that names the destination, with ↗ (or a phone icon for a call). Never a
 * filled pill. `rel` comes from core (sponsored only for a tagged Amazon link); the disclosure line is the caller's.
 */
export function OutsideLink({ link }: { link: { label: string; href: string; mark: "external" | "phone"; newTab: boolean; rel?: string } }) {
  // A new tab is announced (WCAG G201); a call isn't: the phone icon and the tel: link already say it.
  return (
    <a className={`k-out${link.mark === "phone" ? " is-call" : ""}`} href={link.href} {...(link.newTab ? { target: "_blank" } : {})} {...(link.rel ? { rel: link.rel } : {})}>
      {link.mark === "phone" && <Icon name="phone" size={17} />}
      <span className="k-out-label">{link.label}</span>
      {link.mark === "external" && <span className="k-out-mark" aria-hidden="true">↗</span>}
      {link.newTab && <span className="sr-only"> (opens in a new tab)</span>}
    </a>
  );
}

/** Info: a fact. Small label + value. Not tappable. */
export function InfoLine({ label, value, icon = "pin" }: { label: string; value: ReactNode; icon?: IconName }) {
  return <p className="k-info"><Icon name={icon} size={16} /><span className="k-info-label">{label}</span><span className="k-info-value">{value}</span></p>;
}

/* ───────── choices ───────── */

/** Big tappable choice chips (single select). */
export function Chips<T extends string>({ label, options, value, onChange, name, hideLabel }: { label: string; options: { value: T; label: string }[]; value: T | null | undefined; onChange: (v: T) => void; name?: string; hideLabel?: boolean }) {
  const id = useId();
  return (
    <div role="radiogroup" aria-labelledby={id} className="field">
      <div id={id} className={hideLabel ? "sr-only" : "field-label"}>{label}</div>
      <div className="chips">
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} name={name} className="chip" onClick={() => onChange(o.value)}>
            {value === o.value && <Icon name="check" size={16} />}{o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The Yes / No segmented pair: equal halves with each word on one line, stacked full width when a word wouldn't fit (see ButtonPair). */
function SegPair({ compact, children }: { compact?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("seg-stack");
    el.classList.toggle("seg-stack", pairCut([...el.querySelectorAll<HTMLElement>("button")]));
  }, []);
  useLayoutEffect(measure);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : undefined;
    ro?.observe(el);
    void document.fonts?.ready.then(measure);
    return () => ro?.disconnect();
  }, [measure]);
  return <div ref={ref} className={`seg${compact ? " seg-compact" : ""}`}>{children}</div>;
}

/** Yes/No: two large segmented buttons, never a checkbox (DESIGN §3). */
export function YesNo({ label, value, onChange, bad, compact, labelClass, yes = "Yes", no = "No" }: { label: ReactNode; value: boolean | undefined; onChange: (v: boolean) => void; bad?: boolean; compact?: boolean; labelClass?: string; /** The words on the buttons (the Spanish check-in, audit #13). */ yes?: string; no?: string }) {
  const id = useId();
  return (
    <div className={`yesno${bad ? " yesno-bad" : ""}`} role="radiogroup" aria-labelledby={id}>
      <div id={id} className={labelClass ?? "yesno-q"}>{label}</div>
      <SegPair compact={compact}>
        <button type="button" role="radio" aria-checked={value === true} className="seg-yes" onClick={() => onChange(true)}>{value === true && <Icon name="check" size={18} />}{yes}</button>
        <button type="button" role="radio" aria-checked={value === false} className="seg-no" onClick={() => onChange(false)}>{value === false && <Icon name="check" size={18} />}{no}</button>
      </SegPair>
    </div>
  );
}

export function Field({ label, hint, hintOn, error, narrow, children }: { label: string; hint?: ReactNode; /** "focus": the hint shows only while the field is being used (no standing helper line under every field); a screen reader still reads it. */ hintOn?: "focus"; error?: string; /** A short value (a phone, a price, a count): the box is sized to it, not stretched across the row. */ narrow?: boolean; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className={`field${error ? " field-error" : ""}${narrow ? " field-narrow" : ""}`}>
      <label htmlFor={id} className="field-label">{label}</label>
      {children(id)}
      {hint && <div className={`field-hint${hintOn === "focus" ? " field-hint-focus" : ""}`}>{hint}</div>}
      {error && <div className="field-msg" role="alert"><Icon name="alert" size={16} />{error}</div>}
    </div>
  );
}

/* ───────── the seven states ───────── */

/** Loading: a skeleton, not a spinner (UX_SCOPE). */
export function Skeleton({ rows = 3, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div className="skel" role="status" aria-live="polite" aria-label={label}>
      <div className="skel-block" />
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skel-line" style={{ width: `${88 - i * 14}%` }} />)}
    </div>
  );
}
export function Loading({ label = "Loading" }: { label?: string }) { return <Skeleton label={label} />; }

/** A server answer's JSON body is never text for a person: its message, or a plain line. */
export function plainError(text: string): string {
  if (!/^\s*[{[]/.test(text)) return text;
  try { const j = JSON.parse(text) as { error?: { message?: string }; message?: string }; return j.error?.message || j.message || "Something went wrong. Try again."; } catch { return "Something went wrong. Try again."; }
}

/** Error: plain words plus Try again. */
export function ErrorBox({ error: raw, onRetry }: { error?: string; onRetry?: () => void }) {
  if (!raw) return null;
  const error = plainError(raw);
  return (
    <div className="errorbox" role="alert">
      <Icon name="alert" />
      <span>{error}</span>
      {onRetry && <Button variant="secondary" small onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Empty (and success): the mascot, one warm sentence, one action. */
export function Empty({ children, action, mascot = true, wink }: { children: ReactNode; action?: ReactNode; mascot?: boolean; wink?: boolean }) {
  return (
    <div className="empty">
      {mascot && <Mascot wink={wink} />}
      <p>{children}</p>
      {action}
    </div>
  );
}

/** Blocked: why, and what to do. No mascot on stop states. */
export function Blocked({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="blocked" role="alert">
      <h2><Icon name="x" size={26} />{title}</h2>
      {children && <div className="stack-sm">{children}</div>}
      {action && <div className="row">{action}</div>}
    </div>
  );
}

/* ───────── check target with the check draw ───────── */

/** Two or three tiny four-point sparkles that pop once beside a check as it lands (decor, 320 ms, off under Reduce Motion in CSS). */
export function Glints() {
  const star = "M0-5Q.8-.8 5 0Q.8.8 0 5Q-.8.8-5 0Q-.8-.8 0-5Z";
  return (
    <span className="glints" aria-hidden="true" data-testid="glints">
      <svg viewBox="-20 -20 40 40" width="40" height="40">
        <path className="glint g1" d={star} />
        <path className="glint g2" d={star} />
        <path className="glint g3" d={star} />
      </svg>
    </span>
  );
}

export function CheckTarget({ done, onClick, label, kind = "tap", busy }: { done: boolean; onClick?: () => void; label: string; kind?: "tap" | "log"; busy?: boolean }) {
  // Sparkles and the tick draw only when a check LANDS here (open -> done), never for rows that were already done when the screen opened.
  // Open is a <button>, done is a <span>: the done node mounts already in its final state, so a CSS transition never runs on it.
  // The tick is therefore a keyframe animation switched on by `target-fresh`. The ref is read once, so `landed` stays true after the
  // flip: a later re-render (busy ends, the server answers) must not remove the class mid-draw.
  const wasDone = useRef(done);
  const landed = done && !wasDone.current;
  const svg = (
    <svg viewBox="0 0 30 30" aria-hidden="true">
      <circle className="ring" cx="15" cy="15" r="13" />
      <path className="tick" d="M9 15.5l4 4 8-9" />
    </svg>
  );
  if (!onClick || done) return <span className={`target target-static${kind === "log" && !done ? " target-log" : ""}${done ? " on" : ""}${landed ? " target-fresh" : ""}`} aria-hidden={!done} role={done ? "img" : undefined} aria-label={done ? `${label}: done` : undefined}>{svg}{landed && <Glints />}</span>;
  return <button type="button" className={`target${kind === "log" ? " target-log" : ""}`} aria-label={label} onClick={onClick} disabled={busy}>{svg}</button>;
}

/** Steps dots (setup). */
export function Steps({ current, total }: { current: number; total: number }) {
  return <div className="dots" role="img" aria-label={`Step ${current} of ${total}`}>{Array.from({ length: total }, (_, i) => <span key={i} className={i < current ? "on" : ""} />)}</div>;
}

/** Progress bar (one solid water fill). `value` of `max`, e.g. 9 of 12 checks. */
export function Progress({ value, max, label, sub }: { value: number; max: number; label: ReactNode; sub?: ReactNode }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="progress">
      <div className="progress-top">
        <div className="progress-num num">{value} <small>of {max} {label}</small></div>
        {sub}
      </div>
      <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={`${value} of ${max} ${typeof label === "string" ? label : ""}`}>
        <div className="bar-fill" style={barFill(pct / 100)} />
      </div>
    </div>
  );
}

/**
 * One stage of a card or flow (motion audit fix 6). The key is the stage, so a new stage mounts fresh and its content eases in
 * (opacity + 8 px rise, 180 ms). The swap itself is immediate: the new content is on screen and usable on the same frame; only its
 * entrance is decoration. Reduce Motion turns the animation off (styles.css).
 */
export function StageIn({ k, children, className = "stack" }: { k: string | number; children: ReactNode; className?: string }) {
  return <div key={k} className={`${className} stage-in`}>{children}</div>;
}

/* ───────── toasts ───────── */

/** `link`: where the saved record can be seen ("See it in today's records"; audit "Is it working?"). */
interface ToastLink { label: string; to: string }
interface ToastItem { id: number; leaving?: boolean; text: string; icon?: IconName; undo?: () => void | Promise<void>; link?: ToastLink; sub?: string; chop?: boolean }
/**
 * `ms`: how long it stays (default 4 s; with `undo`, the 30 s Undo window). `chop`: the cleaver chops once in place of the
 * icon (a clean log saved from Easy steps, Chop.tsx); `sub`: a second, smaller line under the text.
 */
interface ToastApi {
  show(text: string, opts?: { icon?: IconName; undo?: () => void | Promise<void>; ms?: number; link?: ToastLink; sub?: string; chop?: boolean }): void;
  /**
   * The Landed card (core landed.ts; docs/ux/PEEK-AND-RESUME.md §4.1): what was saved and where it went, in the toast's slot.
   * Use it through components/Landed.tsx useLanded(), which fills in the viewer. Plain toasts stay for things that land nowhere.
   */
  landed(e: LandedEvent, viewer: LandedViewer, opts?: LandedOpts): void;
}
/** `undo`: the card's Undo (the same record as the on-page Undo). `where`: "the stock check"; `back`: "item 4" (the peek's way back). */
export interface LandedOpts { undo?: () => void | Promise<void>; where?: string; back?: string }
/** A peek someone asked for by tapping the card: components/Peek.tsx PeekHost renders it (inside the session and router). */
export interface PeekRequest { events: LandedEvent[]; viewer: LandedViewer; where: string; back: string }
const PeekContext = createContext<{ req: PeekRequest | null; close: () => void }>({ req: null, close: () => {} });
export const usePeekRequest = () => useContext(PeekContext);

/** A toast's link: a router Link inside the app, a plain link where no router is mounted (a test of one component). */
function ToastLinkTo({ link, onGo }: { link: ToastLink; onGo: () => void }) {
  return useInRouterContext() ? <Link className="toast-link" to={link.to} onClick={onGo}>{link.label}</Link> : <a className="toast-link" href={link.to} onClick={onGo}>{link.label}</a>;
}
const ToastContext = createContext<ToastApi | null>(null);

/** How long a leaving toast may take before it is removed even if the browser never reports the end of its exit (styles.css --dur-exit + slack). */
const TOAST_EXIT_FALLBACK_MS = 200;

/**
 * Bottom center, ink background, 4 s; a check-off's toast carries Undo and stays UNDO_SECONDS (30 s, DESIGN §3b).
 * The stack is bottom-anchored and the NEWEST toast goes on top of the list, so a second check-off adds a toast above the first and
 * the first toast (and its Undo) stays under the thumb (motion audit H1). A toast leaves with a 160 ms fade-and-drop instead of
 * vanishing; under Reduce Motion it is removed at once. Up to 3 at a time; a 4th pushes out the oldest.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  // The Landed card: one at a time in the slot (L4); the last dropped one is kept until its time is up so a next save to the
  // same place still merges into it (L3) even though the tap that made it also dropped it (pass-through, spec §4.1).
  const [card, setCard] = useState<{ c: LandedCard; viewer: LandedViewer; opts: Map<string, LandedOpts> } | null>(null);
  const dropped = useRef<{ c: LandedCard; opts: Map<string, LandedOpts> } | null>(null);
  const [peek, setPeek] = useState<PeekRequest | null>(null);
  const next = useRef(1);
  const remove = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);
  const drop = useCallback((id: number) => {
    if (prefersReducedMotion()) { remove(id); return; }
    setItems((l) => l.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => remove(id), TOAST_EXIT_FALLBACK_MS); // if animationend already removed it, this finds nothing
  }, [remove]);
  const api = useMemo<ToastApi>(() => ({
    show(text, opts) {
      const id = next.current++;
      setItems((l) => [{ id, text, ...(opts?.icon ? { icon: opts.icon } : {}), ...(opts?.undo ? { undo: opts.undo } : {}), ...(opts?.link ? { link: opts.link } : {}), ...(opts?.sub ? { sub: opts.sub } : {}), ...(opts?.chop ? { chop: true } : {}) }, ...l.slice(0, 2)]);
      setTimeout(() => drop(id), opts?.ms ?? (opts?.undo ? UNDO_SECONDS * 1000 : 4000));
    },
    landed(e, viewer, opts) {
      const now = Date.now();
      setCard((cur) => {
        const base = cur ? { c: cur.c, opts: cur.opts } : dropped.current && now < dropped.current.c.endsAt ? dropped.current : null;
        dropped.current = null;
        const c = mergeLanded(base?.c ?? null, e, now);
        const keep = base && c.firstAt === base.c.firstAt ? new Map(base.opts) : new Map<string, LandedOpts>();
        keep.set(e.id, opts ?? {});
        return { c, viewer, opts: keep };
      });
    },
  }), [drop]);
  const dropCard = useCallback((stash: boolean) => setCard((cur) => { dropped.current = stash && cur ? { c: cur.c, opts: cur.opts } : null; return null; }), []);
  const peekCtx = useMemo(() => ({ req: peek, close: () => setPeek(null) }), [peek]);
  return (
    <ToastContext.Provider value={api}>
      <PeekContext.Provider value={peekCtx}>
      {children}
      </PeekContext.Provider>
      {/* The Landed card's slot: directly above the tab bar; it covers the "In your hands" bar while it shows (one thing in the slot). */}
      {card && (
        <div className="landed-slot">
          <LandedCardView key={card.c.firstAt} card={card.c} viewer={card.viewer} opts={card.opts}
            onDrop={(stash) => dropCard(stash)}
            onPeek={(where, back) => { setPeek({ events: card.c.events, viewer: card.viewer, where, back }); dropCard(false); }} />
        </div>
      )}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.chop ? " toast-chop" : ""}${t.leaving ? " toast-out" : ""}`} onAnimationEnd={(ev) => { if (t.leaving && ev.animationName === "toastout") remove(t.id); }}>
            {t.chop ? <ChopCleaver /> : <Icon name={t.icon ?? "check"} />}
            {t.sub ? <span className="grow"><strong>{t.text}</strong><span className="toast-sub">{t.sub}</span></span> : <span className="grow">{t.text}</span>}
            {t.link && <ToastLinkTo link={t.link} onGo={() => drop(t.id)} />}
            {t.undo && <button type="button" onClick={() => { void t.undo!(); drop(t.id); }}>Undo</button>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? { show: () => {}, landed: () => {} };
}

/* ───────── sheet / modal ───────── */

/** How long a leaving sheet may take before onClose runs even if the browser never reports the end of its exit (--dur-exit + slack). */
const SHEET_EXIT_FALLBACK_MS = 200;

/** The sheet on top (sheets can stack: Learn how opens over Easy steps); only it answers Esc and traps Tab. */
export const isTopSheet = (el: Element | null) => { const all = document.querySelectorAll(".sheet"); return !!el && all[all.length - 1] === el; };

/**
 * Slides up from the bottom on the phone, centered card on web; Esc or the close button closes it.
 * `full`: fills the screen (the Easy steps player), `closeLabel` names the close button when it does more than close.
 */
/**
 * The page behind open sheets doesn't scroll. Counted, not saved-and-restored per sheet: with two sheets (or one closing while
 * another opens, e.g. tapping "Find it on Amazon" in a sheet), restoring each sheet's saved value could leave the page stuck at
 * overflow:hidden after everything closed (owner, 2026-10-08: "scrolling vertically breaks if clicking buy from amazon").
 */
let sheetLocks = 0;
let pageOverflow = "";
export function lockPageScroll() {
  if (sheetLocks++ === 0) { pageOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; }
}
export function unlockPageScroll() {
  if (sheetLocks === 0) return;
  if (--sheetLocks === 0) document.body.style.overflow = pageOverflow;
}

export function Sheet({ title, kicker, onClose, children, full, wide, closeLabel = "Close" }: { title: ReactNode; kicker?: ReactNode; onClose: () => void; children: ReactNode; full?: boolean; /** A wider card on a big screen (tables); the phone is the same sheet. */ wide?: boolean; closeLabel?: string }) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  // Exit (motion audit fix 7): close, Esc and a tap outside first play the 160 ms exit, then call onClose once (animationend, or the
  // timer if the browser never reports it). Reduce Motion closes at once. While it leaves, the sheet no longer takes taps.
  const [closing, setClosing] = useState(false);
  const closed = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // The parent may decline a close (the photo editor ignores it while saving). Then the sheet is still mounted, and still holds the
  // page lock, but would sit invisible and untappable at the end of its exit animation: the page behind looked fine and would not
  // scroll (owner, 2026-10-09: "inner scroll is locked" on /app/supplies/setup). If it is still here 150 ms after onClose ran (an accepted close unmounts it well inside that), it comes back.
  const finish = useCallback(() => {
    if (closed.current) return;
    closed.current = true; onClose();
    setTimeout(() => { if (mounted.current) { closed.current = false; setClosing(false); } }, 150);
  }, [onClose]);
  const requestClose = useCallback(() => {
    if (closed.current) return;
    if (prefersReducedMotion()) { finish(); return; }
    setClosing(true);
  }, [finish]);
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(finish, SHEET_EXIT_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [closing, finish]);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input, button:not(.sheet-close), select, textarea, [tabindex]");
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (!isTopSheet(ref.current)) return;
      if (e.key === "Escape") { e.preventDefault(); requestClose(); }
      if (e.key === "Tab" && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>("a[href], button:not(:disabled), input, select, textarea, [tabindex]:not([tabindex='-1'])")];
        if (!f.length) return;
        const [a, b] = [f[0]!, f[f.length - 1]!];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); b.focus(); }
        else if (!e.shiftKey && document.activeElement === b) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const unforward = scrimRef.current && ref.current ? installSheetScroll(scrimRef.current, ref.current, () => isTopSheet(ref.current)) : undefined;
    return () => { unforward?.(); document.removeEventListener("keydown", onKey); prev?.focus?.({ preventScroll: true }); };
  }, [requestClose]);
  // The page lock lives exactly as long as the sheet is in the DOM: taken in the commit that adds it, given back in the commit that
  // removes it. As a passive effect the release ran a tick after the sheet was gone, and it was also dropped and retaken on every
  // re-render that brought a new onClose.
  useLayoutEffect(() => { lockPageScroll(); return unlockPageScroll; }, []);
  return createPortal(
    <div ref={scrimRef} className={`scrim${full ? " scrim-full" : ""}${closing ? " scrim-out" : ""}`} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div className={`sheet${full ? " sheet-full" : ""}${wide ? " sheet-wide" : ""}${closing ? " sheet-out" : ""}`} role="dialog" aria-modal="true" aria-labelledby={id} ref={ref}
        onAnimationEnd={(e) => { if (closing && e.target === e.currentTarget && e.animationName.startsWith("sheetout")) finish(); }}>
        <div className="grabber" aria-hidden="true" />
        <div className="sheet-head">
          <div>
            {kicker && <p className="kicker">{kicker}</p>}
            <h2 id={id}>{title}</h2>
          </div>
          <button type="button" className="sheet-close" aria-label={closeLabel} onClick={requestClose}><Icon name="x" /></button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
